/**
 * RuntimeFormRenderer — renders a form from ConfigObject + FormSchema.
 *
 * Uses the shared FormRenderer in 'app' mode with real data binding.
 * Handles: load record, save, delete, field rendering by attribute type.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useAppLocale } from '@/app/permissions/useAppLocale'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputAdornment from '@mui/material/InputAdornment'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import HistoryIcon from '@mui/icons-material/History'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import type { FormNode, FormSchema } from '../designer/types/form.types'
import { FormRenderer } from '../shared/FormRenderer'
// Leaf import on purpose — every RuntimeFormRenderer test mocks the
// FormRenderer component module, which would make this resolver undefined.
import { resolveInputFieldAttrName } from '../shared/FormRenderer/resolveInputFieldAttrName'
import { RefFieldWidget } from './RefFieldWidget'
import { CompositeFieldWidget } from './CompositeFieldWidget'
import { TablePartField } from './TablePartField'
import { FormattedNumberField, resolveNumberDigits } from './number'
import { InputFieldElement } from '../designer/components/editors/FormEditor/elements/InputFieldElement'
import { TableElement } from '../designer/components/editors/FormEditor/elements/TableElement'
import { resolveFormSchema } from './resolveFormSchema'
import { schemaHasCommandBar, schemaHasRootCommandBar, appRecordPath } from './entityRoutes'
import type { ConfigObject, AttributeDef } from '../designer/services/configObjectApi'
import { configObjectApi } from '../designer/services/configObjectApi'
import { TranslatableTextField } from './TranslatableTextField'
import apiClient from '@/utils/api'
import { resolveAttrMaxLength } from '@/shared/resolveAttrMaxLength'
import { runtimeApi } from '@/app/services/runtimeApi'
import Snackbar from '@mui/material/Snackbar'
import { AttachmentPanel } from './AttachmentPanel'
import { FileDropOverlay } from './FileDropOverlay'
import { useAttachmentUpload } from './useAttachmentUpload'
import { HistoryPanel } from './HistoryPanel'
import { MovementsPanel } from './MovementsPanel'
import { RoleRightsPanel } from './RoleRightsPanel'
import { UsersLanguageField } from './UsersLanguageField'
import { JournalPreviewPanel } from './JournalPreviewPanel'
import {
  splitFormSchemaForJournalPreview,
  isJournalPreviewTable,
} from './formSchemaJournalPreview'
import { useJournalPreviewRows } from './useJournalPreviewRows'
import { DateTimeField } from '../shared/DateTimeField'
import {
  resolveDateTimeFieldMode,
  resolveRuntimeInputType,
  formatDateToStored,
} from '../shared/fieldInputTypes'
import { showAppAttributeTypeLabel } from '../shared/showAppAttributeTypeLabel'
import {
  usePrintCommands,
  printCommandToOption,
  PrintLayoutPickerMenu,
  type PrintLayoutOption,
} from '@/runtime/print'
import { hasActivePrintWindow } from './print/printWindowBus'
import { CommandBar, StatusChip, type CommandItem } from '@/shared/components'
import { prefsStore } from '@/app/preferences/prefsStore'
import {
  LOCKED_PRIMARY_IDS,
  commandPinKey,
  parsePinPrefs,
  resolvePinSlot,
  type PinSlot,
} from '@/shared/form/commandPin'
import { pageRowCountsFromData } from '@/shared/form/pagesModel'
import { BaseFormShell } from '@/shared/forms'
import { useWorkspaceTabChromeHost } from '@/app/workspace/workspaceTabChrome'
import { deriveRecordHeader } from './deriveRecordHeader'
import { parseRequiredFieldErrors } from './parseRequiredFieldErrors'
import { notifyRecordsChanged } from './recordsChanged'
import { askInteractionDialog, withInteractionAnswers } from './interactionLoop'
import { formatAuditDate } from './formatAuditDate'
import { formatAuditCreatedBy } from './formatAuditCreatedBy'
import { HierarchyKindIcon } from '@/shared/HierarchyKindIcon'
import { isPredefinedRecord } from './appHiddenAttributes'
import { resolveLabel, mergeAttributeSynonyms } from './resolveLabel'
import { useFieldEvents, type FieldEventTarget } from './fieldEvents'
import { canWriteRecord as canWriteRecordFor, type MyPermissions } from '@/app/permissions/permissionHelpers'
import { TempPasswordDialog } from './TempPasswordDialog'
import { MicroLabel, StatusLine } from '@/shared/operator'

/** Same true/1/'true'/'1' normalization as `useHierarchyTree`. */
function isGroupValue(v: unknown): boolean {
  return v === true || v === 1 || v === 'true' || v === '1'
}

interface RuntimeFormRendererProps {
  connectionId: string
  objectName: string
  recordId?: string | null
  formName?: string
  /**
   * Seed values merged into a NEW record's initial data (e.g. the hierarchy
   * Create menu's `parent_id`/`is_group`, spec §2.5). Ignored when opening an
   * existing record (`recordId` set) and applied after form-default/auto-number
   * seeding so an explicit caller seed always wins.
   */
  initialValues?: Record<string, unknown> | null
  onClose?: () => void
  /**
   * `willClose` is set for Save-and-close so the host can skip replacePath +
   * navigate(/id) — those race with onClose and resurrect the tab (first click
   * saves, second click finally closes).
   */
  onSaved?: (record: Record<string, any>, opts?: { willClose?: boolean }) => void
  /** Fired after a successful delete from the form (before `onClose`). */
  onDeleted?: (recordId: string) => void
  /** Workspace tab dirty flag — optional; omitted outside App workspace. */
  onDirtyChange?: (dirty: boolean) => void
  /** Workspace tab title — optional. */
  onTitleChange?: (title: string) => void
  /** Workspace tab status chip (Draft/Posted) — optional. */
  onStatusChange?: (status: string | undefined) => void
  /** Lift title-row chrome into AppTabBar when the workspace tab is active. */
  liftChrome?: boolean
  /** Momentary modal↔tab toggle — never writes the schema. */
  onTogglePresentation?: () => void
  presentation?: 'tab' | 'modal'
  /** Effective rights on this connection (Task 5/12, ERP Users & Access Rights
   * epic). Optional — omitted, save/delete/reset-password commands behave
   * exactly as before this epic (fail-open `canCreate`/`canUpdate` on `undefined`). */
  permissions?: MyPermissions
}

const READONLY_SAVE_KEYS = new Set(['id', 'created_at', 'updated_at', 'created_by'])

/**
 * Strip presentation/runtime keys; never send autonumber preview on update.
 *
 * Final-review C1: `config.attributes` here is NOT the raw ConfigObject —
 * every real load path (generated AND custom forms, `resolveFormSchema.ts` +
 * the `/form/item` fetch above) overwrites it with `/form/item`'s
 * sibling-FREE `attributes` payload (`_serialize_form_attributes` deliberately
 * excludes both translation siblings and composite `{name}_type` siblings —
 * they're internal storage, never their own form field). The comment this
 * function used to carry ("config.attributes already carries a translatable
 * base field's generated siblings") was true only against the UNFILTERED
 * ConfigObject and has been false since the sibling-free overwrite landed —
 * both a composite pair's discriminator and a translatable field's popover
 * values were silently dropped from every App-form save. Derive the sibling
 * allow-list from the base attrs that DO survive the filter (composite_ref /
 * translatable), restoring in computed form the sibling-name check the
 * 2026-07-28 translatable epic deleted as (wrongly) redundant.
 */
export function buildRuntimeSavePayload(
  data: Record<string, any>,
  config: ConfigObject,
  autoNumberField: string | null,
  isUpdate: boolean,
): Record<string, any> {
  const attrNames = new Set(config.attributes.map((a) => a.name))
  const compositeSiblingKeys = new Set(
    config.attributes
      .filter((a) => a.attr_type === 'composite_ref')
      .map((a) => `${a.name}_type`),
  )
  const translatableAttrNames = new Set(
    config.attributes.filter((a) => a.translatable).map((a) => a.name),
  )
  const out: Record<string, any> = {}
  for (const [k, v] of Object.entries(data)) {
    if (k.endsWith('_display') || k.startsWith('_')) continue
    if (isUpdate && READONLY_SAVE_KEYS.has(k)) continue
    if (autoNumberField && k === autoNumberField) continue
    if (attrNames.has(k) || compositeSiblingKeys.has(k)) {
      out[k] = v
      continue
    }
    // Hierarchy seed (Create→Group|Element): is_group is platform-internal,
    // so /form/item never lists it in config.attributes. parent_id usually
    // survives the filter, but a custom schema can omit it too — keep both.
    if (k === 'is_group' || k === 'parent_id') {
      out[k] = v
      continue
    }
    // Translation sibling `<base>__<locale>` (Task 4 popover) — the base-attr
    // allow-list above only matches the base name itself.
    const sepIdx = k.indexOf('__')
    if (sepIdx > 0 && translatableAttrNames.has(k.slice(0, sepIdx))) {
      out[k] = v
    }
  }
  return out
}

/**
 * Render a field widget based on attribute type — applies all designer properties.
 */
function FieldWidget({
  attr,
  value,
  onChange,
  disabled,
  node,
  connectionId,
  onOpenRef,
  selfObjectName,
  formValues,
  onFieldChange,
  onCompositeFire,
}: {
  attr: AttributeDef | undefined
  value: any
  onChange: (v: any) => void
  disabled: boolean
  node?: FormNode
  connectionId?: string
  onOpenRef?: (target: string, id: string) => void
  selfObjectName?: string
  /** Whole record + keyed setter — only used by the translatable-string branch
   * (Task 4), which reads/writes `<field>__<locale>` sibling keys directly. */
  formValues?: Record<string, any>
  onFieldChange?: (key: string, value: any) => void
  /** Composite pair (Task 6 final-review I1): the generic `onChange`'s
   * immediate field-event fire only ever carries the value key — for a
   * composite discriminator switch that patch would send a fresh value under
   * the STALE type. Fired instead, with both fresh keys, right after the
   * paired state write. */
  onCompositeFire?: (value: unknown, typeName: string | null) => void
}) {
  // Designer tree historically stored "reference"; config API uses "ref".
  const rawType = String(attr?.attr_type ?? 'string').toLowerCase()
  const type = rawType === 'reference' ? 'ref' : rawType
  const maxLength = resolveAttrMaxLength(attr)
  const template = attr?.props?.template as string | undefined
  const placeholder = node?.placeholder || template || undefined
  const readOnly = !!node?.readOnly
  const isRequired = !!node?.required || !!attr?.required
  const suffix = node?.inputSuffix
  const prefixAdorn = node?.inputPrefix
  const startAdornment = prefixAdorn ? <InputAdornment position="start">{prefixAdorn}</InputAdornment> : undefined
  const endAdornment = suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : undefined

  // Composite-type attribute WITH a declared type set — grouped multi-target
  // picker + primitive candidates (Task 6). A composite_ref with no set (or
  // an empty one) falls through to the plain RefFieldWidget branch below,
  // same as before this widget existed.
  if (
    type === 'composite_ref' &&
    Array.isArray(attr?.props?.composite_types) &&
    attr!.props!.composite_types.length > 0 &&
    attr &&
    connectionId
  ) {
    const typeKey = `${attr.name}_type`
    return (
      <CompositeFieldWidget
        attr={attr}
        value={value}
        typeValue={formValues?.[typeKey]}
        onChangePair={(v, t) => {
          onChange(v)
          onFieldChange?.(typeKey, t)
          // Final-review I1: fire the field event with BOTH fresh keys —
          // `onChange`'s own immediate fire (suppressed for composite below)
          // would otherwise send the new value under the STALE type.
          onCompositeFire?.(v, t)
        }}
        connectionId={connectionId}
        disabled={disabled || readOnly}
        onOpenRef={onOpenRef}
      />
    )
  }

  if ((type === 'ref' || type === 'enum_ref' || type === 'composite_ref') && attr && connectionId) {
    return (
      <RefFieldWidget
        attr={attr}
        value={value}
        onChange={onChange}
        disabled={disabled || readOnly}
        connectionId={connectionId}
        isRequired={isRequired}
        onOpenRef={onOpenRef}
        selfObjectName={selfObjectName}
      />
    )
  }

  if (type === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled || readOnly}
            size="small"
          />
        }
        label=""
      />
    )
  }

  if (type === 'number') {
    const { precision, scale } = resolveNumberDigits(attr?.props)
    return (
      <FormattedNumberField
        value={value == null || value === '' ? null : Number(value)}
        onChange={onChange}
        precision={precision}
        scale={scale}
        disabled={disabled}
        required={isRequired}
        readOnly={readOnly}
        size="small"
        fullWidth
        variant="outlined"
        placeholder={placeholder}
        startAdornment={prefixAdorn}
        endAdornment={suffix}
        inputProps={{
          ...(node?.minValue != null ? { min: node.minValue } : {}),
          ...(node?.maxValue != null ? { max: node.maxValue } : {}),
        }}
      />
    )
  }

  const format = attr?.props?.format as string | undefined
  const dateMode = resolveDateTimeFieldMode(type, format)
  if (dateMode) {
    return (
      <DateTimeField
        mode={dateMode}
        attrType={type}
        format={format}
        value={value}
        onChange={(next) => onChange(next)}
        disabled={disabled}
        required={isRequired}
        readOnly={readOnly}
        size="small"
        fullWidth
      />
    )
  }

  if (type === 'text' || node?.multiline) {
    return (
      <TextField
        value={value ?? ''}
        onChange={(e) => {
          let v = e.target.value
          if (maxLength != null && v.length > maxLength) v = v.slice(0, maxLength)
          onChange(v)
        }}
        disabled={disabled}
        required={isRequired}
        size="small"
        fullWidth
        multiline
        rows={node?.rows ?? 3}
        placeholder={placeholder}
        inputProps={{ maxLength: maxLength ?? undefined }}
        InputProps={{ readOnly, startAdornment, endAdornment }}
      />
    )
  }

  // Translatable string field (Task 4): base column + a per-language popover
  // writing `<field>__<code>` sibling keys — needs the whole record + a keyed
  // setter, not just this field's own value/onChange pair.
  if (attr?.translatable && formValues && onFieldChange) {
    return (
      <TranslatableTextField
        field={attr.name}
        values={formValues}
        onChange={onFieldChange}
        disabled={disabled}
        required={isRequired}
        readOnly={readOnly}
        placeholder={placeholder}
        maxLength={maxLength ?? undefined}
        type={resolveRuntimeInputType(type, format, node?.inputType)}
        startAdornment={startAdornment}
        endAdornment={endAdornment}
      />
    )
  }

  // Default: string / ref / enum_ref / etc.
  return (
    <TextField
      value={value ?? ''}
      onChange={(e) => {
        let v = e.target.value
        if (maxLength != null && v.length > maxLength) v = v.slice(0, maxLength)
        onChange(v)
      }}
      disabled={disabled}
      required={isRequired}
      size="small"
      fullWidth
      type={resolveRuntimeInputType(type, format, node?.inputType)}
      inputProps={{ maxLength: maxLength ?? undefined }}
      placeholder={placeholder}
      InputProps={{ readOnly, startAdornment, endAdornment }}
    />
  )
}

export const RuntimeFormRenderer: React.FC<RuntimeFormRendererProps> = ({
  connectionId,
  objectName,
  recordId,
  formName,
  initialValues,
  onClose,
  onSaved,
  onDeleted,
  onDirtyChange,
  onTitleChange,
  onStatusChange,
  liftChrome = false,
  onTogglePresentation,
  presentation,
  permissions,
}) => {
  const navigate = useNavigate()
  const tabChromeHost = useWorkspaceTabChromeHost()
  const { locale, defaultCode, languages: appLanguages, loading: permsLoading } = useAppLocale()
  // Last identity we actually fetched — locale-only changes on a dirty form
  // must not wipe typed values (T9). Identity changes still reload.
  const fetchedIdentityRef = useRef<string | null>(null)
  const [config, setConfig] = useState<ConfigObject | null>(null)
  const [formSchema, setFormSchema] = useState<FormSchema | null>(null)
  const [data, setData] = useState<Record<string, any>>({})
  const [autoNumberField, setAutoNumberField] = useState<string | null>(null)
  // After create, store the new record ID so attachments tab appears
  const [savedRecordId, setSavedRecordId] = useState<string | null>(null)
  const effectiveRecordId = savedRecordId || recordId || null
  // Save write-gate (Task 12) — routed through the single `canWriteRecord`
  // helper (permissionHelpers.ts) so Save/Delete/Reset-password all agree on
  // whether "Users" is gated by `isUserAdmin` or by the plain per-op RBAC
  // check. See that helper's doc comment for why Users can't use a literal
  // create/update check.
  const canWriteRecord = canWriteRecordFor(permissions, objectName, effectiveRecordId ? 'update' : 'create')
  /** One-time password surfaced either by "Reset password" or a `_temp_password`
   * key riding a save result (Task 4 — a Users create that also minted a
   * platform account). Never logged, never persisted beyond this state. */
  const [tempPassword, setTempPassword] = useState<string | null>(null)
  const [resettingPassword, setResettingPassword] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  // Field-events (on_change) needs the LATEST data/saving state inside async
  // callbacks that outlive a render (fired from blur/change handlers) —
  // updated every render, read via useFieldEvents' getData/isSaving.
  const dataRef = useRef(data)
  dataRef.current = data
  const savingRef = useRef(false)
  const [eventNotice, setEventNotice] = useState<{ severity: 'warning' | 'info'; text: string } | null>(
    null,
  )
  const [dirty, setDirty] = useState(false)
  const [pendingOpenRef, setPendingOpenRef] = useState<{ target: string; id: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** Non-fatal warning surfaced from on_post (document saved but movements failed). */
  const [postWarning, setPostWarning] = useState<string | null>(null)
  /**
   * Save-time error (validation rejections like "Group accounts cannot be
   * used..."). Deliberately separate from `error` (load/delete failures):
   * the form is fully rendered and usable at save time, so this renders as
   * an inline Alert above the form (same idiom as postWarningAlert) instead
   * of unmounting the whole form via the `if (error) return <Alert>` guard.
   */
  const [saveError, setSaveError] = useState<string | null>(null)
  /** Per-field save rejections (required fields) — rendered under each field. */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [activeTab, setActiveTab] = useState(0)
  const [fileCount, setFileCount] = useState(0)
  const [journalReloadKey, setJournalReloadKey] = useState(0)
  const [attachSnack, setAttachSnack] = useState<string | null>(null)
  const [attachmentsReloadKey, setAttachmentsReloadKey] = useState(0)
  /** Resolved Users.name/email for the audit footer — never a raw platform user id. */
  const [auditCreatedByLabel, setAuditCreatedByLabel] = useState('')
  const [pinRevision, setPinRevision] = useState(0)
  const attachmentOwner = useMemo(
    () => ({
      connectionId,
      ownerType: config?.obj_type || '',
      ownerName: objectName,
      ownerId: effectiveRecordId || '',
    }),
    [connectionId, config?.obj_type, objectName, effectiveRecordId],
  )
  const { uploadFiles: uploadDroppedFiles } = useAttachmentUpload(attachmentOwner)
  const handleAttachFiles = useCallback(
    async (files: File[]) => {
      const uploaded = await uploadDroppedFiles(files)
      if (uploaded > 0) {
        setAttachSnack(`${uploaded} file(s) attached`)
        setAttachmentsReloadKey((k) => k + 1)
      } else {
        setAttachSnack('Upload failed')
      }
    },
    [uploadDroppedFiles],
  )

  // Load config + form + data
  useEffect(() => {
    // After create Save the host renames /new → /id (recordId prop catches up to
    // savedRecordId we already applied from the create response). Reloading would
    // flash loading + refetch the same row — skip that catch-up only.
    // savedRecordId intentionally omitted from deps: setting it after create must
    // not re-run this effect while recordId is still null.
    if (
      savedRecordId != null &&
      recordId != null &&
      String(recordId) === String(savedRecordId)
    ) {
      return
    }
    // useAppLocale reads perms; while they load, locale is null. Fetching now
    // would stick *_display / ref text to the no-locale (base) payload.
    if (permsLoading) return

    const identityKey = `${connectionId}\0${objectName}\0${recordId ?? ''}\0${formName ?? ''}`
    const localeOnly = fetchedIdentityRef.current === identityKey
    // dirty is read, not a dep: a locale flip on a clean form refetches;
    // a locale flip on a dirty form must not wipe typed values.
    if (dirty && localeOnly) return

    fetchedIdentityRef.current = identityKey

    let cancelled = false
    setLoading(true)
    setError(null)
    // Identity change (connection/object/record): stale dirty + pending ↗ must not carry over.
    setDirty(false)
    setPendingOpenRef(null)

    ;(async () => {
      // Three independent legs, fired together and awaited in the ORIGINAL
      // order — the sequence below still runs config → form → attributes →
      // record, but the network time overlaps instead of stacking.
      // The attributes call is NOT among them: it needs the resolved form name.
      const cfgPromise = configObjectApi.getObject(connectionId, objectName)
      const formPromise = resolveFormSchema(connectionId, objectName, 'item', formName || undefined)
      const recordPromise = recordId
        ? runtimeApi.get(connectionId, objectName, recordId, locale ?? '')
        : null
      // Fetched for new records only; a 404 here is expected (no autonumbering).
      const nextNumberPromise = recordId
        ? null
        : apiClient
            .get<{ field: string; value: string | number }>(
              `/v1/app/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/next-number`,
            )
            .catch(() => null)
      // A leg that rejects while we are still awaiting an earlier one would
      // otherwise surface as an unhandled rejection; awaiting it below still
      // throws into the same catch as before.
      recordPromise?.catch(() => {})

      try {
        const cfg = await cfgPromise
        if (cancelled) return
        setConfig(cfg)

        // Load form schema through the single resolver: custom from designerTree
        // (isDefaultForm, or the named form when `formName` is given) → generated
        // fallback (fresh attributes straight from designerTree).
        const resolved = await formPromise
        const fName = resolved?.schema?.name || formName || 'ItemForm'

        if (resolved?.schema?.root?.length) {
          setFormSchema(resolved.schema)
          if (resolved.source === 'custom') {
            // Same attribute source as auto-gen forms (designerTree) — drives date/ref/number widgets.
            // /form/item now returns attributes even for custom schemas (format included).
            try {
              const formQs = new URLSearchParams()
              if (fName !== 'ItemForm' && fName !== 'ListForm') formQs.set('form_name', fName)
              const qs = formQs.toString()
              const attrResp = await apiClient.get(
                `/v1/app/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/form/item${qs ? `?${qs}` : ''}`,
              )
              const attrBody = attrResp.data as { attributes?: AttributeDef[] }
              if (attrBody?.attributes && Array.isArray(attrBody.attributes) && attrBody.attributes.length > 0) {
                cfg.attributes = attrBody.attributes
                if (!cancelled) setConfig({ ...cfg })
              }
            } catch {
              // keep configObject attributes
            }
          } else if (resolved.attributes && Array.isArray(resolved.attributes)) {
            cfg.attributes = resolved.attributes
            if (!cancelled) setConfig({ ...cfg })
          }
        } else {
          // Last resort: same BE factory as Designer New Form / App defaults.
          const { loadGeneratedFormSchema } = await import(
            '../designer/services/formGeneration'
          )
          const generated = await loadGeneratedFormSchema(
            connectionId,
            objectName,
            'item',
          )
          if (!cancelled) setFormSchema(generated)
        }

        // Load record data OR fetch auto-number preview for new records
        if (recordPromise) {
          const record = await recordPromise
          if (!cancelled) setData(record || {})
        } else {
          // New record: apply defaultValue from the resolved custom form's fields
          // (generated/fallback schemas never carry designer-authored defaults).
          const loadedForm =
            resolved?.source === 'custom' && resolved.schema?.root?.length ? resolved.schema : null
          if (loadedForm) {
            const defaults: Record<string, any> = {}
            const collectDefaults = (nodes: FormNode[]) => {
              for (const n of nodes) {
                if (n.type === 'InputField' && n.defaultValue != null && n.dataPath) {
                  const field = n.dataPath.replace('Object.', '')
                  defaults[field] = n.defaultValue
                }
                if (n.children) collectDefaults(n.children)
              }
            }
            collectDefaults(loadedForm.root as FormNode[])
            if (Object.keys(defaults).length > 0 && !cancelled) {
              setData((prev) => ({ ...defaults, ...prev }))
            }
          }

          // Caller-provided seed (e.g. hierarchy Create menu's parent_id/is_group,
          // spec §2.5) — applied after form defaults so an explicit seed always wins.
          if (initialValues && !cancelled) {
            setData((prev) => ({ ...prev, ...initialValues }))
          }

          // New record: next auto-number preview (fired above, awaited here so
          // it still lands after the form defaults and the caller's seed).
          const nr = (await nextNumberPromise)?.data
          if (!cancelled && nr?.field && nr?.value != null) {
            setAutoNumberField(nr.field)
            setData((prev) => ({ ...prev, [nr.field]: nr.value }))
          }

          // New record on a DOCUMENT: show "now" in the `date` field immediately
          // (1C behavior) rather than relying on the silent server default
          // (create_record fills an absent date too, but only on save — the
          // form must display it before that). Never overrides a value a
          // prior source (defaultValue/initialValues) already seeded; never
          // runs for catalogs or existing records.
          if (cfg.obj_type === 'document' && !cancelled) {
            const dateAttr = cfg.attributes.find(
              (a) => a.name === 'date' && (a.attr_type === 'date' || a.attr_type === 'datetime'),
            )
            if (dateAttr) {
              const mode =
                resolveDateTimeFieldMode(dateAttr.attr_type, dateAttr.props?.format as string | undefined) ??
                'datetime'
              const now = formatDateToStored(new Date(), mode)
              if (now) {
                setData((prev) => (prev.date == null || prev.date === '' ? { ...prev, date: now } : prev))
              }
            }
          }
        }
        // Successful load replaces form state — clear dirty again (edits during flight).
        if (!cancelled) {
          setDirty(false)
          setPendingOpenRef(null)
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load')
      }
      if (!cancelled) setLoading(false)
    })()

    return () => { cancelled = true }
  }, [connectionId, objectName, recordId, formName, initialValues, locale, permsLoading])

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  // Audit footer created_by → Users display name (hooks above early returns).
  useEffect(() => {
    const raw = data.created_by
    if (raw === null || raw === undefined || raw === '') {
      setAuditCreatedByLabel('')
      return
    }
    const id = String(raw).trim()
    if (!/^[0-9a-fA-F]{32}$/.test(id)) {
      setAuditCreatedByLabel(formatAuditCreatedBy(id))
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await runtimeApi.list(connectionId, 'Users', {
          filters: { platform_user_id: id },
          limit: 1,
        })
        const row = (res as { data?: Array<Record<string, unknown>> })?.data?.[0]
        const label = String(row?.name || row?.email || '').trim()
        if (!cancelled) {
          setAuditCreatedByLabel(formatAuditCreatedBy(id, { id, label }))
        }
      } catch {
        if (!cancelled) setAuditCreatedByLabel('')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [connectionId, data.created_by])

  // Workspace tab title / status — must sit above early returns (Rules of Hooks).
  useEffect(() => {
    if (!config || (!onTitleChange && !onStatusChange)) return
    const isDocument = config.obj_type === 'document'
    const header = deriveRecordHeader(config, data, {
      isDocument,
      recordId: effectiveRecordId,
      objectLabel: resolveLabel(
        { synonyms: config.synonyms, synonym: config.synonym, name: objectName },
        locale,
        defaultCode,
      ),
    })
    onTitleChange?.(header.title)
    onStatusChange?.(header.status?.label)
  }, [config, data, effectiveRecordId, objectName, locale, defaultCode, onTitleChange, onStatusChange])

  // Attribute lookup (exact + case-insensitive — custom forms may differ in casing)
  const attrMap = useMemo(() => {
    if (!config) return new Map<string, AttributeDef>()
    const m = new Map<string, AttributeDef>()
    for (const a of config.attributes) {
      m.set(a.name, a)
      const lower = a.name.toLowerCase()
      if (!m.has(lower)) m.set(lower, a)
    }
    return m
  }, [config])

  const lookupAttr = useCallback(
    (name: string | undefined | null): AttributeDef | undefined => {
      if (!name) return undefined
      return attrMap.get(name) ?? attrMap.get(name.toLowerCase())
    },
    [attrMap],
  )

  // Field change handler
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    setDirty(true)
    setData((prev) => ({ ...prev, [fieldName]: value }))
    // Filling the field answers its own error — clear it on edit rather than
    // leaving stale red text until the next save round-trip.
    setFieldErrors((prev) => {
      if (!prev[fieldName]) return prev
      const next = { ...prev }
      delete next[fieldName]
      return next
    })
  }, [])

  // fieldEvents.ts' AttrLike.columns is `... | undefined` (no `null`) —
  // AttributeDef.columns is `... | null`; normalize once rather than widen
  // the shared hook's type for one caller.
  const fieldEventAttributes = useMemo(
    () => config?.attributes.map((a) => ({ ...a, columns: a.columns ?? undefined })),
    [config],
  )

  // Header-field on_change events (Task 4 of the field-events epic): fires
  // the server round-trip on value commit for attributes whose props declare
  // a handler; fails open (see fieldEvents.ts) — an event failure never
  // blocks input, it only surfaces a dismissable notice.
  const fieldEvents = useFieldEvents({
    connectionId,
    objectName,
    attributes: fieldEventAttributes,
    getData: () => dataRef.current,
    applyData: (updater) => setData((prev) => updater(prev)),
    isSaving: () => savingRef.current,
    onError: (text) => setEventNotice({ severity: 'warning', text }),
    onMessages: (ms) => setEventNotice({ severity: 'info', text: ms.join(' · ') }),
  })

  const handleOpenRef = useCallback(
    (target: string, id: string) => {
      if (dirty) {
        setPendingOpenRef({ target, id })
        return
      }
      navigate(appRecordPath(target, id))
    },
    [dirty, navigate],
  )

  const handleConfirmOpenRef = useCallback(() => {
    if (!pendingOpenRef) return
    const { target, id } = pendingOpenRef
    setPendingOpenRef(null)
    setDirty(false)
    navigate(appRecordPath(target, id))
  }, [pendingOpenRef, navigate])

  const handleCancelOpenRef = useCallback(() => {
    setPendingOpenRef(null)
  }, [])

  // Fields the form actually renders — a rejection naming a field that isn't
  // on the form must stay in the alert, or the user would see NOTHING after a
  // failed save.
  const renderedFieldNames = useMemo(() => {
    const names = new Set<string>()
    const walk = (nodes: FormNode[] | undefined) => {
      for (const n of nodes ?? []) {
        if (n.type === 'InputField') {
          const bound = resolveInputFieldAttrName(n)
          if (bound) names.add(bound)
        }
        walk(n.children as FormNode[] | undefined)
      }
    }
    walk(formSchema?.root as FormNode[] | undefined)
    return names
  }, [formSchema])

  // Save (without closing)
  const handleSave = useCallback(async (): Promise<Record<string, any> | null> => {
    if (!config) return null
    setSaving(true)
    savingRef.current = true
    setSaveError(null)
    setFieldErrors({})
    setPostWarning(null)
    try {
      const payload = buildRuntimeSavePayload(data, config, autoNumberField, !!effectiveRecordId)
      const result = await withInteractionAnswers(
        (answers) => {
          const body = {
            ...payload,
            ...(answers.length ? { interaction_answers: answers } : {}),
          }
          if (effectiveRecordId) {
            return runtimeApi.update(connectionId, objectName, effectiveRecordId, body)
          }
          return runtimeApi.create(connectionId, objectName, body)
        },
        askInteractionDialog,
      )
      if (!effectiveRecordId && result?.id) {
        setSavedRecordId(result.id)
      }
      // A Users create that also minted a platform account rides its one-time
      // password back on the create response (Task 4). Pull it out of `result`
      // BEFORE it ever reaches `data` state — `buildRuntimeSavePayload` already
      // strips `_`-prefixed keys on the way to a future save, but a credential
      // has no business sitting in form state a moment longer than it must.
      const { _temp_password: tempPasswordFromResult, ...resultWithoutTempPassword } = result ?? {}
      if (result) {
        setData(resultWithoutTempPassword)
        setDirty(false)
      }
      // Backend emits _post_error when on_post throws — record saved but movements
      // weren't generated. Surface it so the user knows to fix the module code.
      if (typeof result?._post_error === 'string' && result._post_error) {
        setPostWarning(result._post_error)
      }
      // Surfaced exactly once, the same dialog the "Reset password" command feeds.
      if (typeof tempPasswordFromResult === 'string' && tempPasswordFromResult) {
        setTempPassword(tempPasswordFromResult)
      }
      setJournalReloadKey((k) => k + 1)
      // A list of this object may be open in another tab, mounted and stale —
      // it no longer owns this form, so it has no other way to learn of a save.
      notifyRecordsChanged(objectName)
      setSaving(false)
      savingRef.current = false
      return result
    } catch (e: any) {
      const capMessage = 'Too many interactive dialogs in one operation'
      if (e instanceof Error && e.message === capMessage) {
        setSaveError(capMessage)
        setSaving(false)
        savingRef.current = false
        return null
      }
      // ApiService (frontend/src/utils/api.ts) throws a plain { detail,
      // status_code } object, not an Error/axios instance — read `.detail`
      // first so the real server message (e.g. a before_write validation
      // rejection) reaches the user instead of the generic fallback.
      const detail = e?.detail || e?.response?.data?.detail || e?.message || 'Failed to save'
      // The alert always names what failed; a required-fields rejection is also
      // marked under the fields it names, which may be off-screen.
      const perField = parseRequiredFieldErrors(detail, config.attributes)
      if (perField && Object.keys(perField).every((n) => renderedFieldNames.has(n))) {
        setFieldErrors(perField)
      }
      setSaveError(detail)
      setSaving(false)
      savingRef.current = false
      return null
    }
  }, [config, connectionId, objectName, effectiveRecordId, data, autoNumberField, renderedFieldNames])

  // Both save paths end here. When the save minted a platform account, its
  // one-time password rides the response into a dialog THIS component owns —
  // so BOTH callbacks have to wait, not just `onClose` (whole-branch review
  // I2, round 2). Historically `onSaved`→replacePath remounted the host
  // surface (`key={tab.path}`); the host now keys on `surfaceKey`, but
  // Save-and-close still defers until the dialog closes. A ref, not state:
  // nothing renders from it and it must survive the dialog's own re-render.
  const deferredAfterTempPasswordRef = useRef<
    { record: Record<string, any>; close: boolean } | null
  >(null)

  const finishSave = useCallback(
    (result: Record<string, any>, close: boolean) => {
      if (typeof result._temp_password === 'string' && result._temp_password) {
        deferredAfterTempPasswordRef.current = { record: result, close }
        return
      }
      // Sync-clear workspace dirty BEFORE onSaved/onClose. Local setDirty(false)
      // only schedules a React update — the store still had isDirty=true when
      // Save-and-close hit confirmCloseWorkspaceTab, so the user got
      // "Discard unsaved changes?" after a successful save.
      onDirtyChange?.(false)
      onSaved?.(result, { willClose: close })
      if (close) onClose?.()
    },
    [onSaved, onClose, onDirtyChange],
  )

  // Save and close — persist, notify list with the saved row, then leave.
  const handleSaveAndClose = useCallback(async () => {
    const result = await handleSave()
    if (result) finishSave(result, true)
  }, [handleSave, finishSave])

  const handleTempPasswordDialogClose = useCallback(() => {
    setTempPassword(null)
    const deferred = deferredAfterTempPasswordRef.current
    deferredAfterTempPasswordRef.current = null
    if (!deferred) return
    onDirtyChange?.(false)
    onSaved?.(deferred.record, { willClose: deferred.close })
    if (deferred.close) onClose?.()
  }, [onSaved, onClose, onDirtyChange])

  // Delete
  const handleDelete = useCallback(async () => {
    if (!effectiveRecordId) return
    const wasMarked = data?.is_deleted === true || data?.is_deleted === 1
    try {
      await runtimeApi.delete(connectionId, objectName, effectiveRecordId)
      // Soft-update the list tab — bare notify would clear the windowed cache
      // (loadingInitial → skeleton flash) the same way a full reload does.
      notifyRecordsChanged(objectName, {
        reason: 'delete',
        recordId: String(effectiveRecordId),
        wasMarked: Boolean(wasMarked),
      })
      onDeleted?.(String(effectiveRecordId))
      onClose?.()
    } catch (e: any) {
      setError(e?.message || 'Failed to delete')
    }
  }, [connectionId, objectName, effectiveRecordId, data, onDeleted, onClose])

  // Reset password ("Users" only, gated on isUserAdmin — see canWriteRecord's
  // comment above for why this can't be a canUpdate check). Mints a fresh
  // one-time password for the member's linked platform account and hands it
  // to the same TempPasswordDialog a `_temp_password` save result feeds.
  const handleResetPassword = useCallback(async () => {
    if (!effectiveRecordId) return
    setResettingPassword(true)
    try {
      const { temp_password } = await runtimeApi.resetUserPassword(connectionId, effectiveRecordId)
      setTempPassword(temp_password)
    } catch (e: any) {
      setSaveError(e?.detail || e?.response?.data?.detail || e?.message || 'Failed to reset password')
    } finally {
      setResettingPassword(false)
    }
  }, [connectionId, effectiveRecordId])

  // Render field callback for FormRenderer — wraps real input in InputFieldElement chrome (title + layout)
  const renderField = useCallback(
    (attrName: string, node: FormNode, options: { disabled: boolean; mode: 'canvas' | 'preview' | 'app' }) => {
      const attr = lookupAttr(attrName)
      // Auto-number field is read-only (server manages the value)
      const isAutoNum = !effectiveRecordId && autoNumberField === attrName
      // Field-events (on_change) commit style: text-ish widgets commit on
      // blur (typing shouldn't spam the round-trip); pickers/toggles/dates
      // commit immediately on selection, with the fresh value patched in
      // since `data` state hasn't re-rendered yet at the moment of change.
      const rawFieldType = String(attr?.attr_type ?? 'string').toLowerCase()
      const fieldType = rawFieldType === 'reference' ? 'ref' : rawFieldType
      const commitsOnBlur = ['string', 'text', 'number'].includes(fieldType)
      // Composite pairs fire through `onCompositeFire` (both fresh keys in
      // one patch) instead of this generic single-key immediate fire —
      // final-review I1: firing here too would send the fresh value under
      // whatever type happened to be marked-changed first, then consume the
      // change mark before the correct combined fire ever runs.
      const isComposite = fieldType === 'composite_ref'
      const eventTarget: FieldEventTarget = { field: attrName }
      // Users.language is a STRING (not platform_internal) — admin Select
      // may write "". Hidden when the tree has fewer than two languages so
      // there is no empty labelled slot. Caption still comes from
      // InputFieldElement below when the Select is visible.
      if (objectName === 'Users' && attrName === 'language' && appLanguages.length < 2) {
        return null
      }
      const widget =
        objectName === 'Users' && attrName === 'language' ? (
          <UsersLanguageField
            languages={appLanguages}
            value={String(data[attrName] ?? '')}
            onChange={(v) => handleFieldChange(attrName, v)}
            disabled={options.disabled}
          />
        ) : (
          <FieldWidget
            attr={attr}
            value={data[attrName]}
            onChange={(v) => {
              handleFieldChange(attrName, v)
              fieldEvents.markChanged(eventTarget)
              if (!commitsOnBlur && !isComposite) fieldEvents.fireIfChanged(eventTarget, { [attrName]: v })
            }}
            disabled={options.disabled || isAutoNum}
            node={node}
            connectionId={connectionId}
            onOpenRef={handleOpenRef}
            selfObjectName={objectName}
            formValues={data}
            onCompositeFire={(v, t) => {
              const typeKey = `${attrName}_type`
              fieldEvents.fireIfChanged(eventTarget, { [attrName]: v, [typeKey]: t })
            }}
            onFieldChange={(key, v) => {
              handleFieldChange(key, v)
              // Translatable string fields also write `<field>__<locale>`
              // sibling keys through this same callback — only the BASE key
              // is the on_change handler's concern.
              if (key === attrName) fieldEvents.markChanged(eventTarget)
            }}
          />
        )
      // Bubble-phase onBlur, NOT capture: FormattedNumberField (and other
      // widgets that buffer keystrokes) only call their onChange — and thus
      // only markChanged — from their OWN blur handler. A capture-phase
      // listener on this ancestor Box would run BEFORE that target handler,
      // so fireIfChanged would find nothing marked yet. Bubble-phase runs
      // AFTER the target's handlers, once the mark is in place.
      const committedWidget = commitsOnBlur ? (
        <Box sx={{ display: 'contents' }} onBlur={() => fieldEvents.fireIfChanged(eventTarget)}>
          {widget}
        </Box>
      ) : (
        widget
      )
      // InputFieldElement renders title chrome + slot (real editable widget) — WYSIWYG parity.
      // A hand-built custom form node carries no `synonyms` of its own (FormEditor
      // has no per-node synonym UI) — borrow the attribute's map so translations
      // still resolve for customised objects, the Designer's main path.
      const nodeForLabel = mergeAttributeSynonyms(node, attr ? [attr] : undefined)
      const inputElement = (
        <InputFieldElement
          node={nodeForLabel}
          attributeType={attr?.attr_type}
          showAttributeType={showAppAttributeTypeLabel()}
          slot={committedWidget}
          locale={locale}
          defaultLocale={defaultCode}
        />
      )
      // Save rejections render under the field they name, not in a banner
      // above the form (the user must see WHICH field is at fault).
      const fieldError = fieldErrors[attrName]
      const fieldElement = fieldError ? (
        <Box sx={{ width: '100%', minWidth: 0 }}>
          {inputElement}
          <Typography
            variant="caption"
            color="error"
            data-testid={`field-error-${attrName}`}
            sx={{ display: 'block', mt: 0.25 }}
          >
            {fieldError}
          </Typography>
        </Box>
      ) : (
        inputElement
      )
      const isRequiredField = !!node?.required || !!attr?.required
      // Fixed-width gutter for the amber required marker so required vs optional
      // inputs share one left edge (wrapping only required fields used to shove
      // those rows ~12px right of their optional neighbours).
      const labelIsTop = node?.labelPosition === 'top'
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: labelIsTop ? 'flex-start' : 'center',
            gap: 0.75,
            width: '100%',
            minWidth: 0,
          }}
        >
          <Box
            aria-hidden="true"
            data-testid="required-marker-slot"
            sx={{
              width: 6,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: labelIsTop ? 0.5 : 0,
              alignSelf: labelIsTop ? 'flex-start' : 'center',
              minHeight: 6,
            }}
          >
            {isRequiredField ? (
              <Box
                component="span"
                aria-hidden="true"
                data-testid="required-marker"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'warning.main',
                }}
              />
            ) : null}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>{fieldElement}</Box>
        </Box>
      )
    },
    [
      lookupAttr,
      data,
      handleFieldChange,
      autoNumberField,
      effectiveRecordId,
      connectionId,
      handleOpenRef,
      objectName,
      fieldErrors,
      fieldEvents,
      appLanguages,
    ],
  )

  // "Save" only: save record, stay on form (new → switch URL to edit id)
  const handleSaveOnly = useCallback(async () => {
    const result = await handleSave()
    if (result) finishSave(result, false)
  }, [handleSave, finishSave])

  // Keyboard shortcuts the CommandBar advertises (⌘⏎ / ⌘S). Without this the
  // chips would be decorative and ⌘S would trigger the browser's Save-Page
  // dialog. Modal record forms own the focus while open, so a window listener
  // is appropriate; gated on not-saving and no active print overlay — with a
  // print tab on top, silently saving the hidden form would be surprising.
  // (Print tabs don't handle keys themselves, so the browser default applies
  // there; print-side shortcut handling is a follow-up.)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || saving || !canWriteRecord) return
      if (hasActivePrintWindow()) return
      if (e.key === 'Enter') {
        e.preventDefault()
        void handleSaveAndClose()
      } else if (e.key.toLowerCase() === 's') {
        e.preventDefault()
        void handleSaveOnly()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleSaveAndClose, handleSaveOnly, saving, canWriteRecord])

  // Resolve a tabular section's column AttributeDef[] from cfg.attributes — type info
  // (string/number/ref/boolean/...) drives inline editor selection inside TablePartField.
  // NOTE: hook must be declared before any early returns below — rules-of-hooks.
  const formActions = useMemo(
    () => ({
      submit: () => { void handleSaveAndClose() },
      save: () => { void handleSaveOnly() },
      close: () => onClose?.(),
      // Disabling here (rather than only trimming commandPrimary/commandSecondary
      // below) is what actually reaches a schema that owns its own root
      // CommandBar — its Save/Save-and-close buttons are schema-rendered and
      // bound straight to this object, not to the external shell bar's items.
      disabled: saving || !canWriteRecord,
    }),
    [handleSaveAndClose, handleSaveOnly, onClose, saving, canWriteRecord],
  )

  const { generalSchema, journalPreviewGroup, journalPreviewInline } = useMemo(() => {
    if (!formSchema) {
      return {
        generalSchema: null as FormSchema | null,
        journalPreviewGroup: null as FormNode | null,
        journalPreviewInline: false,
      }
    }
    return splitFormSchemaForJournalPreview(formSchema)
  }, [formSchema])

  const effectiveJournalGroup = journalPreviewGroup

  // Ref target for the Lines tabular part's account_id column — resolves account
  // labels in the journal preview generically, from config attributes (no hardcode).
  const chartOfAccountsTarget = useMemo(() => {
    const linesAttr = lookupAttr('Lines')
    const columns = (linesAttr?.columns ?? []) as AttributeDef[]
    const acctCol = columns.find((c) => c.name === 'account_id')
    return (acctCol?.props?.target || acctCol?.props?.referenceTarget || '') as string
  }, [lookupAttr])

  const { rowsByTable: journalRowsByTable } = useJournalPreviewRows({
    connectionId,
    objectName,
    recordId: effectiveRecordId ?? undefined,
    journalGroup: effectiveJournalGroup,
    formRoot: formSchema?.root,
    documentData: data,
    reloadKey: journalReloadKey,
    chartOfAccountsTarget,
  })

  // Print-commands pipeline: objectName is enough (server resolves entity);
  // pass recordId when saved so handlers can be record-scoped. Unsaved forms
  // still discover object-level commands (Print shown disabled).
  const print = usePrintCommands({
    connectionId,
    objectName,
    recordId: effectiveRecordId,
  })
  const printRecord = useMemo(
    () => (effectiveRecordId ? { ...data, id: effectiveRecordId } : null),
    [data, effectiveRecordId],
  )
  const printPickerOptions = useMemo(
    () => print.commands.map(printCommandToOption),
    [print.commands],
  )

  const renderTable = useCallback(
    (name: string, node: FormNode, options: { disabled: boolean; mode: 'canvas' | 'preview' | 'app' }) => {
      if (options.mode !== 'app') return null
      if (isJournalPreviewTable(node)) {
        const rows = journalRowsByTable[name] ?? []
        return (
          <TableElement
            node={{ ...node, staticPreviewRows: rows }}
            preview
          />
        )
      }
      const tabularAttr = lookupAttr(name)
      const columnAttrs = (tabularAttr?.columns ?? []) as AttributeDef[]
      const rows = Array.isArray(data[name]) ? (data[name] as Record<string, any>[]) : []
      return (
        <TablePartField
          node={node}
          rows={rows}
          onChange={(next) => {
            setDirty(true)
            setData((prev) => ({ ...prev, [name]: next }))
          }}
          connectionId={connectionId}
          objectName={objectName}
          columnAttrs={columnAttrs}
          disabled={options.disabled}
          onOpenRef={handleOpenRef}
          onCellCommit={(rowIndex, colName, nextRows) => {
            // The grid only calls onCellCommit when a cell actually changed
            // (editedCellsRef discipline in TabularGrid) — mark+fire together
            // is correct here, unlike header fields where mark/fire split
            // across separate onChange/onBlur moments.
            const target: FieldEventTarget = { field: colName, tabularPart: name, rowIndex }
            fieldEvents.markChanged(target)
            fieldEvents.fireIfChanged(target, { [name]: nextRows })
          }}
        />
      )
    },
    [lookupAttr, data, connectionId, objectName, journalRowsByTable, handleOpenRef, fieldEvents],
  )

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
  if (!config) return <Alert severity="warning" sx={{ m: 2 }}>Object not found</Alert>

  // If no form schema, render a simple auto-generated form
  if (!formSchema) {
    return <Alert severity="info" sx={{ m: 2 }}>Loading form...</Alert>
  }

  const hasRootCommandBar = schemaHasRootCommandBar(formSchema.root)
  const hasAnyCommandBar = schemaHasCommandBar(formSchema.root)
  const showExternalCommandBar = !hasRootCommandBar
  const hasTabs =
    !!config &&
    (config.obj_type === 'catalog' || config.obj_type === 'document')
  const isDocument = !!config && config.obj_type === 'document'
  const filesEnabled = !!effectiveRecordId

  const recordHeader = deriveRecordHeader(config, data, {
    isDocument,
    recordId: effectiveRecordId,
    objectLabel: resolveLabel({ synonyms: config?.synonyms, synonym: config?.synonym, name: objectName }, locale, defaultCode),
  })

  // Predefined (Designer-seeded) lock + hierarchy kind icon next to the title —
  // mirrors the list's leading icons (Predefined hierarchy UX, Task 4/5).
  // Style B: kind before title; lock (+ status) immediately after the label —
  // NOT in the commands cluster (hierarchy polish contract).
  // is_group is stripped from /form/item attributes (platform-internal);
  // traits.hierarchical is the surviving signal on the overwritten config.
  const hierarchical = !!config.traits?.hierarchical
  const isPredefined = isPredefinedRecord(data)
  const titleAdornment = hierarchical ? (
    <HierarchyKindIcon
      kind={isGroupValue(data.is_group) ? 'group' : 'element'}
      locked={isPredefined}
    />
  ) : isPredefined ? (
    <HierarchyKindIcon kind="element" locked />
  ) : undefined
  const presentationToggle =
    onTogglePresentation && (presentation === 'tab' || presentation === 'modal') ? (
      <Tooltip title={presentation === 'modal' ? 'Open as tab' : 'Open as window'}>
        <IconButton
          size="small"
          aria-label={presentation === 'modal' ? 'Open as tab' : 'Open as window'}
          onClick={onTogglePresentation}
          sx={{ color: 'text.secondary' }}
        >
          {presentation === 'modal' ? (
            <OpenInFullIcon fontSize="small" />
          ) : (
            <CloseFullscreenIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    ) : null

  const titleEndAdornment =
    recordHeader.status || presentationToggle ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {recordHeader.status ? (
          <StatusChip status={recordHeader.status.label} label={recordHeader.status.label} />
        ) : null}
        {presentationToggle}
      </Box>
    ) : undefined

  // Audit footer — mono "CREATED <date> · <user> — LAST EDIT <date>" line at the
  // bottom of the form. Hidden for unsaved (new) records and when created_at is
  // missing/unparseable; the created_by/updated_at parts are individually optional
  // so the line never renders a dangling separator around a blank value.
  // created_by label is resolved in the effect above (Users.name/email; never raw id).
  const auditCreatedAt = formatAuditDate(data.created_at)
  const auditUpdatedAt = formatAuditDate(data.updated_at)
  const auditCreatedBy = auditCreatedByLabel
  const showAuditFooter = !!effectiveRecordId && !!auditCreatedAt
  const auditFooter = showAuditFooter ? (
    <Box sx={{ px: 2, py: 0.75 }}>
      <StatusLine>
        {`CREATED ${auditCreatedAt}${auditCreatedBy ? ` · ${auditCreatedBy}` : ''}${
          auditUpdatedAt ? ` — LAST EDIT ${auditUpdatedAt}` : ''
        }`}
      </StatusLine>
    </Box>
  ) : null

  // Commands the schema can never express — built once, then routed to whichever
  // bar actually owns the record: the external shell CommandBar when the schema has
  // no root bar of its own, or appended onto the schema's bar when it does
  // (commandBarExtras below). Never both — see showExternalCommandBar.
  const printCommand: CommandItem | null = print.hasPrintLayouts
    ? {
        id: 'Print',
        label: 'Print',
        onClick: (e: React.MouseEvent<HTMLElement>) => {
          if (!printRecord) return
          print.openLayoutChoice(e.currentTarget, printRecord, 'print')
        },
        disabled: print.printLoading || !effectiveRecordId,
        tooltip: !effectiveRecordId ? 'Save the record first' : undefined,
      }
    : null
  const deleteCommand: CommandItem | null =
    // Predefined (Designer-seeded) records are Designer-only to delete —
    // the App never surfaces the Delete command for them (Do NOT block
    // edit/save: everything else on this form stays fully usable). Delete
    // goes through the SAME Users-aware helper as Save — a plain `canDelete`
    // would hit the identical describe_permissions trap and hide Delete from
    // a user admin on a Users record.
    effectiveRecordId && !isPredefined && canWriteRecordFor(permissions, objectName, 'delete')
      ? { id: 'Delete', label: 'Delete', onClick: handleDelete, color: 'error' as const }
      : null
  // Users only. `canWriteRecordFor(..., 'update')` on a Users object resolves
  // to `isUserAdmin` regardless of the op argument — see the helper.
  const resetPasswordCommand: CommandItem | null =
    objectName === 'Users' && canWriteRecordFor(permissions, objectName, 'update') && Boolean(effectiveRecordId)
      ? { id: 'ResetPassword', label: 'Reset password', onClick: () => { void handleResetPassword() }, disabled: resettingPassword, busy: resettingPassword }
      : null
  // Commands the schema can never express — appended onto whichever bar owns
  // the record (see printCommand's comment above).
  const extraSecondaryCommands: CommandItem[] = [
    ...(printCommand ? [printCommand] : []),
    ...(resetPasswordCommand ? [resetPasswordCommand] : []),
  ]

  const commandPrimary: CommandItem[] = canWriteRecord
    ? [{ id: 'SaveAndClose', label: 'Save and close', onClick: handleSaveAndClose, disabled: saving, busy: saving, kbd: '⌘⏎' }]
    : []
  const commandSecondary: CommandItem[] = [
    ...(canWriteRecord ? [{ id: 'Save', label: 'Save', onClick: handleSaveOnly, disabled: saving, kbd: '⌘S' }] : []),
    ...extraSecondaryCommands,
  ]
  const commandOverflow: CommandItem[] = [
    ...(deleteCommand ? [deleteCommand] : []),
  ]

  const pinKey = commandPinKey(
    connectionId,
    objectName,
    hasRootCommandBar ? 'MainCommandBar' : 'shell',
  )
  const pinPrefs = parsePinPrefs(pinRevision >= 0 ? prefsStore.getItem(pinKey) : null)

  const pinDefaultForCommand = (id: string): PinSlot | undefined => {
    for (const node of formSchema.root) {
      if (node.type !== 'CommandBar') continue
      const child = (node.children ?? []).find(
        (c) => c.type === 'Button' && (c.name === id || c.id === id),
      )
      if (child) return child.pinDefault
    }
    return undefined
  }

  const splitByPin = (items: CommandItem[]) => {
    const primary: CommandItem[] = []
    const secondary: CommandItem[] = []
    const overflow: CommandItem[] = []
    const locked = LOCKED_PRIMARY_IDS as readonly string[]
    for (const item of items) {
      const id = item.id ?? item.label
      const slot = resolvePinSlot(id, pinDefaultForCommand(id), pinPrefs)
      if (slot === 'overflow') overflow.push(item)
      else if (locked.includes(id)) primary.push(item)
      else secondary.push(item)
    }
    return { primary, secondary, overflow }
  }

  const shellSplit = splitByPin([...commandPrimary, ...commandSecondary, ...commandOverflow])
  const extrasSplit = splitByPin([
    ...extraSecondaryCommands,
    ...(deleteCommand ? [deleteCommand] : []),
  ])

  const handlePin = (id: string, slot: PinSlot) => {
    const current = parsePinPrefs(prefsStore.getItem(pinKey)) ?? { pinned: [], overflow: [] }
    const next =
      slot === 'bar'
        ? {
            pinned: [...current.pinned.filter((x) => x !== id), id],
            overflow: current.overflow.filter((x) => x !== id),
          }
        : {
            pinned: current.pinned.filter((x) => x !== id),
            overflow: [...current.overflow.filter((x) => x !== id), id],
          }
    prefsStore.setItem(pinKey, JSON.stringify(next))
    setPinRevision((n) => n + 1)
  }

  const handleResetPins = () => {
    prefsStore.removeItem(pinKey)
    setPinRevision((n) => n + 1)
  }

  // Shell chrome: only true action buttons in `commands`; Style B adornments
  // go through titleAdornment / titleEndAdornment on the title band.
  const shellCommands = showExternalCommandBar ? (
    <CommandBar
      primary={shellSplit.primary}
      secondary={shellSplit.secondary}
      overflow={shellSplit.overflow}
      onPin={handlePin}
      onResetPins={handleResetPins}
    />
  ) : undefined

  // Schema owns the root CommandBar (Save/Close already come from it) — append
  // only what it can't express. FormRenderer applies this to the root CommandBar
  // node only, so a schema with no root bar simply never uses it (no duplication
  // with shellCommands above).
  const commandBarExtras = {
    secondary: extrasSplit.secondary.length ? extrasSplit.secondary : undefined,
    overflow: extrasSplit.overflow.length ? extrasSplit.overflow : undefined,
    onPin: handlePin,
    onResetPins: handleResetPins,
    pinPrefs,
  }

  const postWarningAlert = postWarning ? (
    <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setPostWarning(null)}>
      Document saved, but posting failed: {postWarning}
    </Alert>
  ) : null

  const saveErrorAlert = saveError ? (
    <Alert severity="error" sx={{ mb: 1 }} onClose={() => setSaveError(null)}>
      {saveError}
    </Alert>
  ) : null

  const eventNoticeAlert = eventNotice ? (
    <Alert
      severity={eventNotice.severity}
      onClose={() => setEventNotice(null)}
      sx={{ mb: 1 }}
      data-testid="field-event-notice"
    >
      {eventNotice.text}
    </Alert>
  ) : null

  const hasJournalPreviewTab =
    !!effectiveJournalGroup && isDocument && !journalPreviewInline

  const roleRightsPanel =
    objectName === 'Roles' ? (
      <RoleRightsPanel
        rights={data?.rights}
        roleName={typeof data?.name === 'string' ? data.name : undefined}
      />
    ) : null

  const formBody = (
    <FormRenderer
      schema={generalSchema!}
      mode="app"
      callbacks={{
        renderField,
        renderTable,
        pageRowCounts: pageRowCountsFromData(generalSchema?.root, data),
      }}
      formValues={data}
      formActions={hasAnyCommandBar ? formActions : undefined}
      commandBarExtras={commandBarExtras}
      pageScroll
    />
  )

  const printLayoutPicker = (
    <PrintLayoutPickerMenu
      menu={print.layoutPickMenu}
      options={printPickerOptions}
      onClose={print.closeLayoutPickMenu}
      onPick={(opt: PrintLayoutOption, ctx) => {
        const cmd = print.commands.find((c) => c.id === String(opt.id))
        if (!cmd) return
        // v1: pdf mode shares the print-window path (export lives inside the window).
        void print.runPrint(ctx.record, cmd)
      }}
    />
  )

  const journalTabLabel =
    effectiveJournalGroup?.title?.replace(/^⚡\s*/, '').trim() || 'Écritures'

  const discardOpenRefDialog = (
    <Dialog
      open={pendingOpenRef !== null}
      onClose={handleCancelOpenRef}
      maxWidth="xs"
      fullWidth
      aria-label="Discard unsaved changes"
    >
      <DialogTitle>Discard unsaved changes?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Opening the referenced record will discard unsaved changes on this form.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelOpenRef}>Cancel</Button>
        <Button color="error" variant="contained" onClick={handleConfirmOpenRef}>
          Discard
        </Button>
      </DialogActions>
    </Dialog>
  )

  // One-time password display (Task 12) — fed by "Reset password" or a
  // `_temp_password` save result. Never re-shown once dismissed.
  const tempPasswordDialog = (
    <TempPasswordDialog
      open={!!tempPassword}
      password={tempPassword ?? ''}
      onClose={handleTempPasswordDialogClose}
    />
  )

  const useTabChrome = Boolean(liftChrome && tabChromeHost)
  // Hide the form title whenever this record is the active workspace tab —
  // the tab already shows it. Buttons stay in-row until the chrome host exists.
  const hideFormTitle = Boolean(liftChrome)
  const tabRecordChrome =
    useTabChrome && tabChromeHost
      ? createPortal(
          <Box
            data-testid="workspace-tab-record-chrome"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
          >
            {titleAdornment}
            {shellCommands}
          </Box>,
          tabChromeHost,
        )
      : null

  if (!hasTabs) {
    return (
      <BaseFormShell
        title={recordHeader.title}
        hideTitle={hideFormTitle}
        titleAdornment={useTabChrome ? undefined : titleAdornment}
        titleEndAdornment={useTabChrome ? undefined : titleEndAdornment}
        commands={useTabChrome ? undefined : shellCommands}
        onClose={useTabChrome ? undefined : onClose}
        footer={auditFooter}
        fullHeight
      >
        {tabRecordChrome}
        {saveErrorAlert}
        {eventNoticeAlert}
        {postWarningAlert}
        <Box
          data-testid="record-form-scroll"
          sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          {roleRightsPanel}
          {formBody}
        </Box>
        {printLayoutPicker}
        {discardOpenRefDialog}
        {tempPasswordDialog}
      </BaseFormShell>
    )
  }

  return (
    <FileDropOverlay
      enabled={filesEnabled}
      overlayText="Drop files to attach"
      disabledText="Save the record first to attach files"
      onFiles={handleAttachFiles}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <BaseFormShell
        title={recordHeader.title}
        hideTitle={hideFormTitle}
        titleAdornment={useTabChrome ? undefined : titleAdornment}
        titleEndAdornment={useTabChrome ? undefined : titleEndAdornment}
        commands={useTabChrome ? undefined : shellCommands}
        onClose={useTabChrome ? undefined : onClose}
        footer={auditFooter}
        fullHeight
      >
        {tabRecordChrome}
        {saveErrorAlert}
        {eventNoticeAlert}
        {postWarningAlert}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            minHeight: 36,
            borderBottom: 1,
            borderColor: 'divider',
            px: 0,
            '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none', fontSize: 13 },
          }}
        >
          <Tab label="General" />
          <Tab
            disabled={!filesEnabled}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AttachFileIcon sx={{ fontSize: 16 }} />
                Files
                {fileCount > 0 && <MicroLabel>{fileCount}</MicroLabel>}
              </Box>
            }
          />
          {isDocument && hasJournalPreviewTab && (
            <Tab
              disabled={!filesEnabled}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <MenuBookOutlinedIcon sx={{ fontSize: 16 }} />
                  {journalTabLabel.length > 28 ? 'Écritures' : journalTabLabel}
                </Box>
              }
            />
          )}
          {isDocument && !hasJournalPreviewTab && (
            <Tab
              disabled={!filesEnabled}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <MenuBookOutlinedIcon sx={{ fontSize: 16 }} />
                  {effectiveJournalGroup ? 'Écritures' : 'Movements'}
                </Box>
              }
            />
          )}
          <Tab
            disabled={!filesEnabled}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <HistoryIcon sx={{ fontSize: 16 }} />
                History
              </Box>
            }
          />
        </Tabs>
        <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Box
            data-testid="record-form-scroll"
            sx={{
              display: activeTab === 0 ? 'flex' : 'none',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              py: 1,
            }}
          >
            {roleRightsPanel}
            {formBody}
          </Box>
          <Box
            sx={{
              py: 1,
              display: activeTab === 1 ? 'flex' : 'none',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {filesEnabled && (
              <AttachmentPanel
                connectionId={connectionId}
                ownerType={config!.obj_type}
                ownerName={objectName}
                ownerId={effectiveRecordId!}
                embedded
                reloadKey={attachmentsReloadKey}
                onCountChange={setFileCount}
                currentData={data}
                onApplyExtraction={(header, tabularAppend) => {
                  setDirty(true)
                  setData((prev) => {
                    const next: Record<string, any> = { ...prev, ...header }
                    for (const [part, rows] of Object.entries(tabularAppend)) {
                      const existing = Array.isArray(prev[part]) ? prev[part] : []
                      next[part] = [...existing, ...rows]
                    }
                    return next
                  })
                  setActiveTab(0)
                  setAttachSnack('Fields filled from file — review and save')
                }}
              />
            )}
          </Box>
          {isDocument && (
            <Box sx={{ py: 1, display: activeTab === 2 ? 'block' : 'none' }}>
              {filesEnabled &&
                (effectiveJournalGroup ? (
                  <JournalPreviewPanel
                      connectionId={connectionId}
                      objectName={objectName}
                      recordId={effectiveRecordId!}
                      journalGroup={effectiveJournalGroup}
                      documentData={data}
                      reloadKey={journalReloadKey}
                      chartOfAccountsTarget={chartOfAccountsTarget}
                    />
                ) : (
                  <MovementsPanel
                    connectionId={connectionId}
                    objectName={objectName}
                    recordId={effectiveRecordId!}
                    reloadKey={journalReloadKey}
                  />
                ))}
            </Box>
          )}
          <Box
            sx={{
              py: 1,
              display: activeTab === (isDocument ? 3 : 2) ? 'block' : 'none',
            }}
          >
            {filesEnabled && (
              <HistoryPanel
                connectionId={connectionId}
                objectName={objectName}
                recordId={effectiveRecordId!}
              />
            )}
          </Box>
        </Box>
        {printLayoutPicker}
        {discardOpenRefDialog}
        {tempPasswordDialog}
        <Snackbar
          open={!!attachSnack}
          autoHideDuration={4000}
          onClose={() => setAttachSnack(null)}
          message={attachSnack ?? ''}
        />
      </BaseFormShell>
    </FileDropOverlay>
  )
}
